import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type AdminLogItem = {
  id: number
  admin_id: string
  admin_name?: string | null
  action: string
  target_type?: string | null
  target_id?: string | null
  details?: Record<string, unknown> | null
  ip_address?: string | null
  created_at: string
}

type LogsResponse = {
  logs: AdminLogItem[]
  total: number
  limit: number
  offset: number
}

const AdminLogsContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [logs, setLogs] = useState<AdminLogItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [adminFilter, setAdminFilter] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 20

  const totalPages = useMemo(() => Math.ceil(total / limit), [total, limit])
  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit])

  const fetchLogs = () => {
    setLoading(true)
    setError('')
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    })
    if (actionFilter.trim()) params.set('action', actionFilter.trim())
    if (adminFilter.trim()) params.set('admin_id', adminFilter.trim())
    requestApi<LogsResponse>(`/api/v1/admin/logs?${params.toString()}`)
      .then((payload) => {
        setLogs(payload.logs || [])
        setTotal(payload.total || 0)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.logs.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    fetchLogs()
  }, [offset, isGranted])

  const handleSearch = () => {
    setOffset(0)
    fetchLogs()
  }

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleString()
    } catch (error) {
      return value
    }
  }

  const formatDetails = (details?: Record<string, unknown> | null) => {
    if (!details) return '-'
    const text = JSON.stringify(details)
    if (text.length > 120) {
      return `${text.slice(0, 120)}...`
    }
    return text
  }

  return (
    <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder={t('admin.logs.placeholders.action')}
          />
          <input
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
            placeholder={t('admin.logs.placeholders.admin')}
          />
          <button
            onClick={handleSearch}
            className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
          >
            {t('admin.logs.search')}
          </button>
        </div>

        {loading && <p className="text-sm text-slate-400">{t('admin.logs.loading')}</p>}
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {!loading && !error && (
          <div className="overflow-x-auto border border-slate-200 rounded-2xl">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">{t('admin.logs.table.action')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.logs.table.target')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.logs.table.admin')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.logs.table.details')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.logs.table.ip')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.logs.table.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-slate-700">{log.action}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {log.target_type || '-'} {log.target_id ? `#${log.target_id}` : ''}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{log.admin_name || log.admin_id}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDetails(log.details)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{log.ip_address || '-'}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(log.created_at)}</td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                      {t('admin.logs.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{t('admin.logs.pagination').replace('{current}', String(currentPage)).replace('{total}', String(totalPages))}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
              >
                {t('admin.logs.prev')}
              </button>
              <button
                onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50"
              >
                {t('admin.logs.next')}
              </button>
            </div>
          </div>
        )}
      </div>
  )
}

export const AdminLogsScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.logs.title')} description={t('admin.logs.desc')}>
      <AdminLogsContent />
    </AdminLayout>
  )
}
